import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/", match: (p: string) => p === "/" },
  { label: "电影", to: "/category?type=movie", match: (p: string, s: string) => p === "/category" && s.includes("type=movie") },
  { label: "电视剧", to: "/category?type=tv", match: (p: string, s: string) => p === "/category" && s.includes("type=tv") },
  { label: "关于我们", to: "/about", match: (p: string) => p === "/about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileKeyword, setMobileKeyword] = useState("");

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (value: string) => {
    const word = value.trim();
    if (!word) return;
    navigate(`/search?keyword=${encodeURIComponent(word)}`);
    setKeyword("");
    setMobileKeyword("");
  };

  const isSearchActive = location.pathname === "/search";

  return (
    <>
      <header className="sticky top-0 z-40 w-full bg-background-50/95 backdrop-blur border-b border-background-200">
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center h-16 md:h-20 gap-4">
            <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
              <span className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-primary-600 text-background-50">
                <i className="ri-movie-2-fill text-xl" aria-hidden="true" />
              </span>
              <span className="font-heading font-bold text-lg md:text-xl text-foreground-950 tracking-wide">
                星辰影视
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-1 ml-4">
              {navItems.map((item) => {
                const active = item.match(location.pathname, location.search);
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      active
                        ? "text-primary-700 bg-primary-100"
                        : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            <div className="hidden md:flex items-center ml-auto">
              <div className="flex items-center bg-background-100 rounded-full border border-background-200 pl-4 pr-1 h-10 w-56 lg:w-72">
                <i className="ri-search-line text-foreground-500 text-base" aria-hidden="true" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitSearch(keyword);
                  }}
                  placeholder="搜索电影、电视剧"
                  className="flex-1 bg-transparent px-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                />
                <button
                  type="button"
                  onClick={() => submitSearch(keyword)}
                  className="h-8 px-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  搜索
                </button>
              </div>
              {isSearchActive && (
                <span className="ml-3 text-xs text-foreground-500 whitespace-nowrap">搜索结果</span>
              )}
            </div>

            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setMenuOpen(true)}
              className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-menu-line text-2xl" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setMenuOpen(false)}
          />
          <aside className="absolute top-0 right-0 h-full w-72 max-w-[80%] bg-background-50 flex flex-col">
            <div className="flex items-center justify-between h-16 px-5 border-b border-background-200">
              <span className="font-heading font-bold text-foreground-950">星辰影视</span>
              <button
                type="button"
                aria-label="关闭菜单"
                onClick={() => setMenuOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl" aria-hidden="true" />
              </button>
            </div>
            <div className="p-5">
              <div className="flex items-center bg-background-100 rounded-full border border-background-200 pl-4 pr-1 h-10">
                <i className="ri-search-line text-foreground-500" aria-hidden="true" />
                <input
                  value={mobileKeyword}
                  onChange={(e) => setMobileKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitSearch(mobileKeyword);
                  }}
                  placeholder="搜索影视作品"
                  className="flex-1 bg-transparent px-3 text-sm outline-none placeholder:text-foreground-400"
                />
                <button
                  type="button"
                  onClick={() => submitSearch(mobileKeyword)}
                  className="h-8 px-3 rounded-full bg-primary-500 text-background-50 text-xs cursor-pointer whitespace-nowrap"
                >
                  搜索
                </button>
              </div>
            </div>
            <nav className="flex flex-col px-3 gap-1">
              {navItems.map((item) => {
                const active = item.match(location.pathname, location.search);
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    className={`px-4 py-3 rounded-md text-sm font-medium cursor-pointer ${
                      active ? "text-primary-700 bg-primary-100" : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
            <div className="mt-auto p-5 text-xs text-foreground-500 border-t border-background-200">
              <p>星辰影视 · 影视内容展示平台</p>
              <p className="mt-1">contact@xingchen-media.com</p>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}